import { useEffect, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select } from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { api, apiError } from "@/services/api"
import type { CalendarEntry, CalendarEntryCreate, CalendarEntryType, Tagtyp } from "@/types"
import { DAY_TYPE_META, DAY_TYPE_ORDER } from "./dayTypes"

interface EntryEditorSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Vorhandener Eintrag = Bearbeiten; null = Neuanlage */
  entry: CalendarEntry | null
}

/** Kalender-Abweichung anlegen/bearbeiten: HINWEIS oder OVERRIDE (mit Tagtyp-Select). */
export function EntryEditorSheet({ open, onOpenChange, entry }: EntryEditorSheetProps) {
  const queryClient = useQueryClient()
  const [entryType, setEntryType] = useState<CalendarEntryType>("HINWEIS")
  const [dateFrom, setDateFrom] = useState("")
  const [dateTo, setDateTo] = useState("")
  const [name, setName] = useState("")
  const [note, setNote] = useState("")
  const [dayTypeOverride, setDayTypeOverride] = useState<Tagtyp>("SONDERTAG")

  useEffect(() => {
    if (!open) return
    setEntryType(entry?.entryType ?? "HINWEIS")
    setDateFrom(entry?.dateFrom ?? "")
    setDateTo(entry?.dateTo ?? "")
    setName(entry?.name ?? "")
    setNote(entry?.note ?? "")
    setDayTypeOverride(entry?.dayTypeOverride ?? "SONDERTAG")
  }, [open, entry])

  const mutation = useMutation({
    mutationFn: (body: CalendarEntryCreate) =>
      entry ? api.calendar.entries.update(entry.id, body) : api.calendar.entries.create(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["calendar"] })
      toast.success(entry ? "Eintrag aktualisiert" : "Eintrag angelegt")
      onOpenChange(false)
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const valid = dateFrom !== "" && dateTo !== "" && dateFrom <= dateTo

  function submit() {
    mutation.mutate({
      dateFrom,
      dateTo,
      entryType,
      name: name.trim() || null,
      note: note.trim() || null,
      dayTypeOverride: entryType === "OVERRIDE" ? dayTypeOverride : null,
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent sheetKey="calendar-entry" className="overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{entry ? "Eintrag bearbeiten" : "Neuer Kalender-Eintrag"}</SheetTitle>
          <SheetDescription>
            Hinweis (reine Anzeige) oder Tagtyp-Override für einen Zeitraum. Betriebsurlaub bitte
            über den eigenen Sheet pflegen.
          </SheetDescription>
        </SheetHeader>
        <div className="grid gap-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="entry-type">Typ</Label>
            <Select
              id="entry-type"
              value={entryType}
              onChange={(e) => setEntryType(e.target.value as CalendarEntryType)}
              disabled={entry?.entryType === "BETRIEBSURLAUB"}
            >
              {entry?.entryType === "BETRIEBSURLAUB" && (
                <option value="BETRIEBSURLAUB">Betriebsurlaub</option>
              )}
              <option value="HINWEIS">Hinweis</option>
              <option value="OVERRIDE">Tagtyp-Override</option>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="entry-from">Von</Label>
              <Input
                id="entry-from"
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="entry-to">Bis</Label>
              <Input
                id="entry-to"
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
          </div>
          {entryType === "OVERRIDE" && (
            <div className="space-y-1.5">
              <Label htmlFor="entry-override">Tagtyp-Override</Label>
              <Select
                id="entry-override"
                value={dayTypeOverride}
                onChange={(e) => setDayTypeOverride(e.target.value as Tagtyp)}
              >
                {DAY_TYPE_ORDER.map((t) => (
                  <option key={t} value={t}>
                    {DAY_TYPE_META[t].label}
                  </option>
                ))}
              </Select>
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="entry-name">Name (optional)</Label>
            <Input
              id="entry-name"
              placeholder="z. B. Inventur"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="entry-note">Hinweis (optional)</Label>
            <Textarea
              id="entry-note"
              placeholder="z. B. Betriebsversammlung 10:00 Uhr"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>
        <SheetFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Abbrechen
          </Button>
          <Button type="button" disabled={!valid || mutation.isPending} onClick={submit}>
            Speichern
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
